import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ImmutablePureComponent from 'react-immutable-pure-component'
import { is } from 'immutable'
import videojs from 'video.js'
import { isTouch } from '../utils/is_mobile'
import { isPanoramic, isPortrait, minimumAspectRatio, maximumAspectRatio } from '../utils/media_aspect_ratio'
import { displayMedia } from '../initial_state'
import { CX, MEDIA_SINGLE_MAX_HEIGHT } from '../constants'
import Button from './button'
import SensitiveMediaItem from './sensitive_media_item'
import { parse, format } from 'url'

import '!style-loader!css-loader!video.js/dist/video-js.min.css'
import '!style-loader!css-loader!@silvermine/videojs-quality-selector/dist/css/quality-selector.css'
import Blurhash from './blurhash'
require('@silvermine/videojs-quality-selector')(videojs);

const isTouchable = isTouch()
const CONTROLS_HEIGHT = 40;

window.videoQuality = 'HD';

if (!window.videoVolume) {
  window.videoVolume = localStorage.getItem('volume') || 0.5
}

const getVideoJSOptions = ({
  autoplay = undefined,
  muted = undefined,
  sources = [{}],
  controls = undefined,
  volume,
}) => ({
  autoplay,
  muted,
  sources,
  playbackRates: [0.5, 1, 1.5, 2],
  controls: true,
  volume,
  type: 'video/mp4',
  responsive: true
})

class Video extends ImmutablePureComponent {

  state = {
    changed: false,
    containerWidth: this.props.width,
    revealed: this.props.visible !== undefined ? this.props.visible : (displayMedia !== 'hide_all' && !this.props.sensitive || displayMedia === 'show_all'),
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.revealed && !this.state.revealed && !!this.video) {
      this.video.pause()
    }
    if (this.props.autoplay !== prevProps.autoplay && !!this.video) {
      if (this.props.autoplay) {
        this.video.play()
      }
      else {
        this.video.pause()
      }
    }
    if (this.props.sourceMp4 !== prevProps.sourceMp4 || this.props.src !== prevProps.src) {
      // this.setState({ changed: true })
    }
  }

  componentWillUnmount() {
    if (this.videoPlayer) {
      this.videoPlayer.dispose()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!is(nextProps.visible, this.props.visible) && nextProps.visible !== undefined) {
      this.setState({ revealed: nextProps.visible })
    }
  }

  handleClickIfMuted() {
    if (this.videoPlayer && typeof this.props.muted !== 'undefined' && this.props.muted) {
      if(this.videoPlayer.muted()) {
        this.videoPlayer.muted(false); 
      }
    }
  }

  setPlayerRef = (n) => {
    this.player = n
    if (n) this.setState({ containerWidth: n.offsetWidth })
  }

  setVideoRef = (n) => {
    this.video = n
    this.setupVideo()
  }

  setupVideo = () => {
    if (!this.video) return null
    const {
      autoplay,
      fileContentType,
      muted,
      sourceMp4,
      lowresMp4,
      src,
      shouldStopAllOtherPlayers,
      controls,
    } = this.props

    let srcs = [
      {
        src: sourceMp4,
        type: 'video/mp4',
        label: 'HD',
        res: 720,
        selected: window.videoQuality === 'HD',
      }
    ]

    if (lowresMp4) {
      srcs.push({
        src: lowresMp4,
        type: 'video/mp4',
        label: 'SD',
        res: 360,
        selected: window.videoQuality === 'SD',
      })
    }
    
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const makeMuted = isSafari ? (typeof muted !== 'undefined' || typeof autoplay !== 'undefined') ? true : undefined : typeof muted !== 'undefined' ? true : undefined

    this.videoPlayer = videojs(this.video, getVideoJSOptions({
      autoplay: typeof autoplay !== 'undefined' ? 'play' : undefined,
      muted: typeof muted !== 'undefined' ? true : undefined,
      controls: typeof controls !== 'undefined' ? true : undefined,
      sources: srcs,
      volume: window.videoVolume,
    }))

    this.videoPlayer.controlBar.addChild('QualitySelector', {}, 15)

    if (!isTouchable) {
      let fullscreen = this.videoPlayer.controlBar.getChild("FullscreenToggle")
      let index = this.videoPlayer.controlBar.children().indexOf(fullscreen)
      let downloadBtn = this.videoPlayer.controlBar.addChild('button', {}, index)
      let downloadBtnEl = downloadBtn.el()
      downloadBtnEl.style.cursor = 'pointer'
      downloadBtnEl.innerHTML = `<i style="font-size:20px;transform:rotateZ(180deg) translateY(2px);" class="${[_s.d, _s.cWhite, _s.gfi, _s['gfi-share']].join(' ')}"></i>`
      downloadBtn.controlText('Download video')
      downloadBtnEl.onclick = function() {
        const href = `${sourceMp4 || src}`.replace('media.', '')
        const a = document.createElement('a')
        a.href = href
        const fileName = sourceMp4.split('/').pop()
        a.download = `gab-video-${fileName}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }

    if (controls === false) {
      this.videoPlayer.controls(false)
    }

    this.shouldStopAllOtherPlayers = shouldStopAllOtherPlayers
    this.videoPlayer.on('play', () => {
      if (this.shouldStopAllOtherPlayers) {
        this.stopAllOtherPlayers()
      }
      if (window.videoVolume) {
        this.videoPlayer.volume(window.videoVolume)
      }
      let cr = this.videoPlayer.currentSources()
      let targetLabel;
      cr.forEach((x) => {
        if (x.selected) {
          targetLabel = x.label
        }
      })
      if (window.videoQuality != targetLabel) {
        window.videoQuality = targetLabel
        localStorage.setItem('quality', targetLabel)
      }
    })

    this.videoPlayer.on('pause', () => {
      if (window.videoVolume != this.videoPlayer.volume()) {
        window.videoVolume = this.videoPlayer.volume()
        localStorage.setItem('volume', this.videoPlayer.volume())
      }
    })

    this.videoPlayer.on('ended', () => {
      if (window.videoVolume != this.videoPlayer.volume()) {
        window.videoVolume = this.videoPlayer.volume()
        localStorage.setItem('volume', this.videoPlayer.volume())
      }
    })
  }

  handleClickRoot = (e) => {
    if (this.props.autoplay) e.stopPropagation()
  }

  toggleReveal = () => {
    if (this.props.onToggleVisibility) {
      this.props.onToggleVisibility()
    } else {
      this.setState({ revealed: !this.state.revealed })
    }
  }

  stopAllOtherPlayers = () => {
    if (!this.props.shouldStopAllOtherPlayers) return

    const thisPlayerId = this.videoPlayer?.id()
    const allPlayers = document.querySelectorAll('.video-js')

    allPlayers.forEach((playerEl) => {
      const playerElId = playerEl.getAttribute('id')
      if (playerElId !== thisPlayerId) {
        const otherVideo = playerEl.querySelector('video')
        if (otherVideo) otherVideo.pause()
      }
    })
  }

  render() {
    const {
      src,
      inline,
      startTime,
      autoplay,
      alt,
      detailed,
      sensitive,
      aspectRatio,
      className,
      blurhash,
      loop,
      size,
      width,
      height,
      fileContentType,
      preview,
    } = this.props
    const {
      revealed,
      containerWidth,
    } = this.state

    let preload = startTime ? 'auto' : (detailed ? 'metadata' : 'none')

    if (!revealed && sensitive) {
      return (
        <SensitiveMediaItem
          onClick={this.toggleReveal}
          message='The author of this gab has added a warning to this video.'
          btnTitle='View'
        />
      )
    }

    const containerStyles = CX(className, {
      d: 1,
      w100PC: 1,
      h100PC: 1,
      outlineNone: 1,
      overflowHidden: 1,
    })

    // Check if preview URL is valid (not a webm extension and exists)
    const hasValidPreview = preview && !preview.endsWith('.webm') && preview.indexOf('missing') === -1
    let posterUrl = hasValidPreview ? preview : undefined
    
    if (!posterUrl) {
      preload = 'auto'
    }

    const isInModal = detailed || className?.includes('media-gallery-item-in-modal')
    
    const style = {
      position: 'relative',
      width: '100%',
      height: isInModal ? '90vh' : '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }

    const videoStyle = {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }

    const playerStyle = {
      width: '100%',
      height: '100%',
      position: 'relative',
      ...(isInModal ? { height: '90vh' } : {})
    }

    return (
      <div
        className={containerStyles}
        style={style}
        ref={this.setPlayerRef}
        onClick={this.handleClickRoot}
        tabIndex={0}
        key={src}
      >
        <div data-vjs-player style={playerStyle}>
          {blurhash && (
            <div id='vjs-blurhash' className={[_s.d, _s.top0, _s.right0, _s.bottom0, _s.left0, _s.posAbs, _s.h100PC, _s.w100PC, _s.z1].join(' ')}>
              <Blurhash hash={blurhash} className={[_s.d, _s.top0, _s.right0, _s.bottom0, _s.left0, _s.posAbs, _s.h100PC, _s.w100PC].join(' ')} />
            </div>
          )}
          <video
            className={[_s.d, _s.h100PC, _s.w100PC, _s.outlineNone, 'video-js'].join(' ')}
            ref={this.setVideoRef}
            playsInline
            loop={loop}
            poster={posterUrl}
            preload={preload}
            role='button'
            tabIndex='0'
            aria-label={alt}
            title={alt}
            style={videoStyle}
            onClick={this.handleClickIfMuted.bind(this)}
          />
        </div>

        {revealed && sensitive &&
          <div className={[_s.posAbs, _s.z2, _s.top0, _s.right0, _s.mt10, _s.mr10].join(' ')}>
            <Button
              title='Hide media'
              icon='hidden'
              backgroundColor='black'
              className={[_s.px10, _s.bgBlackOpaque_onHover].join(' ')}
              onClick={this.toggleReveal}
            />
          </div>
        }
      </div>
    )
  }
}

Video.propTypes = {
  preview: PropTypes.string,
  sourceMp4: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  sensitive: PropTypes.bool,
  startTime: PropTypes.number,
  detailed: PropTypes.bool,
  inline: PropTypes.bool,
  visible: PropTypes.bool,
  onToggleVisibility: PropTypes.func,
  blurhash: PropTypes.string,
  classes: PropTypes.array,
  aspectRatio: PropTypes.number,
  meta: ImmutablePropTypes.map,
  fileContentType: PropTypes.string,
  autoplay: PropTypes.string,
  muted: PropTypes.string,
  shouldStopAllOtherPlayers: PropTypes.bool,
  controls: PropTypes.bool,
  loop: PropTypes.bool,
  size: PropTypes.number,
}

export default Video
